import React, { useEffect, useState } from 'react'
import { Image } from 'react-bootstrap-icons'
import { ReactSortable } from 'react-sortablejs'

import rotatePartImage from '../../../modules/redux/createUpdateForm/rotatePartImage'
import { useDispatch } from '../../../modules/store/customDispatch'
import { PartImage } from '../../../types/part/partTypes'
import ImageRotateActionsBar from '../ImageRotateActionsBar/ImageRotateActionsBar'
import ModalCarousel from '../ModalCarousel/ModalCarousel'
import ModalImageThumb from '../ModalImageThumb/ModalImageThumb'

import styles from './styles.module.scss'

interface IProps {
    images: PartImage[]
    setImages?: (images: PartImage[]) => void
    markImg?: (id: string) => void
    unmarkImg?: (id: string) => void
    showMainImg?: boolean
    partId?: string
    deferDbUpdate?: boolean
    onImageRotated?: (oldImageId: string, newImage: PartImage) => void
}

const ModalImageGroup = ({
    images,
    setImages,
    markImg,
    unmarkImg,
    showMainImg = true,
    partId,
    deferDbUpdate = false,
    onImageRotated,
}: IProps) => {
    const dispatch = useDispatch()
    const [activeImageIndex, setActiveImageIndex] = useState(0)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [pendingRotations, setPendingRotations] = useState<Record<string, number>>({})
    const [isSavingRotations, setIsSavingRotations] = useState(false)

    const pendingCount = Object.keys(pendingRotations).length

    const setActiveImage = (index: number) => {
        setActiveImageIndex(index)
        setIsModalOpen(true)
    }

    const handleImageRotated = (oldImageId: string, newImage: PartImage, currentImages: PartImage[]) => {
        const updated = currentImages.map(img => (img.id === oldImageId ? newImage : img))
        if (setImages) {
            setImages(updated)
        }
        onImageRotated?.(oldImageId, newImage)
        return updated
    }

    const handleRotate = (imageId: string) => {
        setPendingRotations(prev => {
            const currentAngle = prev[imageId] ?? 0
            const newAngle = (currentAngle + 90) % 360

            if (newAngle === 0) {
                const next = { ...prev }
                delete next[imageId]
                return next
            }

            return { ...prev, [imageId]: newAngle }
        })
    }

    const handleCancelRotations = () => {
        setPendingRotations({})
    }

    const handleSaveRotations = async () => {
        if (pendingCount === 0) {
            return
        }

        setIsSavingRotations(true)
        try {
            const toSave = Object.entries(pendingRotations)
                .map(([imageId, angle]) => ({
                    image: images.find(img => img.id === imageId),
                    angle,
                }))
                .filter((item): item is { image: PartImage; angle: number } => Boolean(item.image && item.angle > 0))

            let currentImages = images

            for (const { image, angle } of toSave) {
                const { newImage, oldImageId } = await dispatch(
                    rotatePartImage({
                        partId,
                        oldImage: image,
                        angle,
                        deferDbUpdate,
                    }),
                )

                currentImages = handleImageRotated(oldImageId, newImage, currentImages)
            }

            setPendingRotations({})
        } catch (e) {
            console.error('rotate part images error', e)
        } finally {
            setIsSavingRotations(false)
        }
    }

    useEffect(() => {
        setPendingRotations(prev => {
            const imageIds = new Set(images.map(img => img.id))
            const next: Record<string, number> = {}

            Object.entries(prev).forEach(([imageId, angle]) => {
                if (imageIds.has(imageId) && angle > 0) {
                    next[imageId] = angle
                }
            })

            return next
        })
    }, [images])

    return (
        <div className={styles.modalImageGroup__wrap}>
            {images.length === 0 && showMainImg ? (
                <div className={styles.modalImageGroup__placeholder}>
                    <Image color={'#ced4da'} size={180} />
                    <p>Добавьте фотографии запчасти, чтобы видеть их тут</p>
                </div>
            ) : (
                <>
                    {showMainImg && (
                        <div className={styles.modalImageGroup__main}>
                            <img src={images[activeImageIndex].publicUrl} alt={images[activeImageIndex].id} />
                        </div>
                    )}
                    <div className={styles.modalImageGroup__all}>
                        {setImages ? (
                            <ReactSortable
                                list={images}
                                setList={setImages}
                                className={'justify-content-start'}
                                disabled={isSavingRotations}
                            >
                                {images.map((image, index) => (
                                    <div className={styles.imageWrap} key={image.id}>
                                        <ModalImageThumb
                                            key={image.id}
                                            markImg={markImg && markImg}
                                            unmarkImg={unmarkImg && unmarkImg}
                                            setActiveImageIndex={setActiveImage}
                                            index={index}
                                            id={image.id}
                                            publicUrl={image.publicUrl}
                                            previewAngle={pendingRotations[image.id] ?? 0}
                                            isSaving={isSavingRotations}
                                            onRotate={handleRotate}
                                        />
                                    </div>
                                ))}
                            </ReactSortable>
                        ) : (
                            <div>
                                {images.map((image, index) => (
                                    <div className={styles.imageWrap} key={image.id}>
                                        <ModalImageThumb
                                            key={image.id}
                                            setActiveImageIndex={setActiveImage}
                                            index={index}
                                            id={image.id}
                                            publicUrl={image.publicUrl}
                                            previewAngle={pendingRotations[image.id] ?? 0}
                                            isSaving={isSavingRotations}
                                            onRotate={handleRotate}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <ImageRotateActionsBar
                        count={pendingCount}
                        isSaving={isSavingRotations}
                        onSave={handleSaveRotations}
                        onCancel={handleCancelRotations}
                    />
                </>
            )}
            {!showMainImg && (
                <ModalCarousel
                    closeModal={() => setIsModalOpen(false)}
                    pictures={images}
                    isOpen={isModalOpen}
                    pictureIndex={activeImageIndex}
                />
            )}
        </div>
    )
}

export default ModalImageGroup
